const HTTP_STATUS = {
  FORBIDDEN: 403,
  GATEWAY_TIMEOUT: 504,
  OK: 200
}

const SUCCESS_BIZ_CODE = 10000

/**
 * Handles the HTTP response and returns the appropriate data based on the status code.
 *
 * @param {Response} response - The HTTP response object.
 * @returns {Promise<Object>} - A promise that resolves to the response data or an error message.
 *
 * @example
 * fetch('/api/data')
 *   .then(handleHttpResponse)
 *   .then(data => console.log(data))
 *   .catch(error => console.error(error));
 */
export const handleHttpResponse = async (response, encrypted = false) => {
  const { status } = response
  if (response.ok && response.status === HTTP_STATUS.OK) {
    if (encrypted) {
      return response.text()
    } else {
      return response.json()
    }
  } else if (status === HTTP_STATUS.FORBIDDEN || status === HTTP_STATUS.GATEWAY_TIMEOUT) {
    const message = await response.text()
    return { message }
  } else {
    try {
      const { error } = await response.json()
      return { message: error, status }
    } catch (error) {
      return { message: error.message, status }
    }
  }
}

/**
 * Parses the response data.
 *
 * @param {Object} res - The response object.
 * @param {boolean} self - A flag indicating whether to resolve self.
 * @param {number} res.bizCode - The business code (10000 for success).
 * @param {*} res.data - The response data.
 * @param {string} res.message - The error message if bizCode is not 10000.
 * @returns {Object} The parsed data or an error object.
 */
export const parseResponseData = (res, self) => {
  if (self) {
    // resolve self
    return { data: res }
  } else {
    const { bizCode, data, message } = res
    if (bizCode === SUCCESS_BIZ_CODE) {
      return { data }
    } else {
      const error = new Error(message)
      error.bizCode = bizCode
      console.error(error)
      return {
        error
      }
    }
  }
}
